import axios, { type AxiosInstance } from "axios"
import { getExhibitionHeader } from "../utils/exhibitionContext";
import type { DateRangeAnalytics } from "../Component/Application/Analytics/types"

const api: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://127.0.0.1:8000",
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 10000,
})

// Attach token before each request
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("accessToken")
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`
    Object.assign(config.headers, getExhibitionHeader());
    }
    return config
  },
  (error) => Promise.reject(error),
)

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("accessToken")
      window.location.href = "/login"
    }
    return Promise.reject(error)
  },
)

const AnalyticsService = {
  // Get analytics data for date range - single view function
  async getDateRangeAnalytics(startDate?: string, endDate?: string): Promise<DateRangeAnalytics> {
    try {
      const params = new URLSearchParams()

      if (startDate) {
        params.append("start_date", startDate)
      }

      if (endDate) {
        params.append("end_date", endDate)
      }

      // If no dates provided, default to last 7 days
      if (!startDate && !endDate) {
        const end = new Date()
        const start = new Date()
        start.setDate(start.getDate() - 7)

        params.append("start_date", start.toISOString().split("T")[0])
        params.append("end_date", end.toISOString().split("T")[0])
      }

      const response = await api.get(`/api/entries/date-range-analytics/?${params.toString()}`)

      const djangoData = response.data

      // Transform the response structure to match DateRangeAnalytics interface
      const transformedData: DateRangeAnalytics = {
        // Map Django summary fields to frontend expected field names
        totalEntries: djangoData.summary?.rangeEntries || 0,
        totalExits: djangoData.summary?.rangeExits || 0,
        totalVehicleRevenue: djangoData.summary?.rangeVehicleRevenue || 0,
        totalVerifications: djangoData.summary?.rangeVerifications || 0,
        totalBadgePrints: djangoData.summary?.rangeTotalBadgePrints || 0,
        totalFreeOriginalBadges: djangoData.summary?.rangeFreeOriginalBadges || 0,
        totalPaidCopyBadges: djangoData.summary?.rangePaidCopyBadges || 0,
        totalPaidCopyBadgeRevenue: djangoData.summary?.rangePaidCopyBadgeRevenue || 0,
        totalTicketSalesCount: djangoData.summary?.rangeTicketSalesCount || 0,
        totalTicketRevenue: djangoData.summary?.rangeTicketRevenue || 0,
        totalUsedTickets: djangoData.summary?.rangeUsedTickets || 0,
        totalUnusedTickets: djangoData.summary?.rangeUnusedTickets || 0,
        totalPaidCheckouts: djangoData.summary?.rangePaidCheckouts || 0,
        totalWhitelistedCheckouts: djangoData.summary?.rangeWhitelistedCheckouts || 0,

        // Transform daily data into chart data arrays
        ticketTrends: (djangoData.dailyData || []).map((day: any) => ({
          date: day.date,
          newBought: day.tickets?.sales || 0,
          used: 0, // Not provided in Django response, could be added later
          unused: 0, // Not provided in Django response, could be added later
        })),

        vehicleTrends: (djangoData.dailyData || []).map((day: any) => ({
          date: day.date,
          entered: day.vehicles?.entries || 0,
          exited: day.vehicles?.exits || 0,
        })),

        badgeVerifications: (djangoData.dailyData || []).map((day: any) => ({
          date: day.date,
          verifications: day.badges?.verifications || 0,
        })),
      }

      return transformedData
    } catch (error) {
      console.error("Error fetching date range analytics:", error)
      throw new Error("Failed to fetch analytics data")
    }
  },
}

export default AnalyticsService
